import { 
  collection, 
  doc, 
  addDoc, 
  onSnapshot, 
  query, 
  where, 
  getDocs
} from 'firebase/firestore';
import { db, auth, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

export interface Price {
  id: string;
  unit_amount: number;
  currency: string;
  interval: 'month' | 'year';
  interval_count: number;
}

export interface Product {
  id: string;
  active: boolean;
  name: string;
  description?: string;
  metadata: Record<string, string>;
  prices: Price[];
}

export interface Subscription {
  id: string;
  status: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid';
  current_period_end: { seconds: number };
  current_period_start: { seconds: number };
  cancel_at_period_end: boolean;
  created: { seconds: number };
  price: {
    id: string;
    product: string;
  };
}

// Get products from Firestore (synced from Stripe)
export const getProducts = async (): Promise<Product[]> => {
  const productsRef = collection(db, 'products');
  const q = query(productsRef, where('active', '==', true));
  const querySnapshot = await getDocs(q);
  
  const products: Product[] = [];
  
  for (const doc of querySnapshot.docs) {
    const productData = doc.data();
    
    // Get prices subcollection
    const pricesRef = collection(db, 'products', doc.id, 'prices');
    const pricesSnapshot = await getDocs(pricesRef);
    
    const prices: Price[] = pricesSnapshot.docs.map(priceDoc => ({
      id: priceDoc.id,
      ...priceDoc.data()
    } as Price));
    
    products.push({
      id: doc.id,
      ...productData,
      prices
    } as Product);
  }
  
  return products;
};

// Create a checkout session
// TODO: this should be updaed to trigger a print with callback url. 
export const createCheckoutSession = async (priceId: string): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  
  const checkoutSessionRef = collection(db, 'customers', user.uid, 'checkout_sessions');
  
  const docRef = await addDoc(checkoutSessionRef, {
    price: priceId,
    success_url: `${window.location.origin}/dashboard?subscription=success`,
    cancel_url: `${window.location.origin}/settings`,
    mode: 'subscription',
    metadata: {
      userId: user.uid
    }
  });
  
  // Wait for the extension to create a Stripe Checkout session
  return new Promise((resolve, reject) => {
    const unsubscribe = onSnapshot(doc(db, 'customers', user.uid, 'checkout_sessions', docRef.id), 
      (snap) => {
        const data = snap.data();
        if (data?.error) {
          unsubscribe();
          reject(new Error(data.error.message));
        }
        if (data?.url) {
          unsubscribe();
          resolve(data.url);
        }
      }
    );
  });
};

// Get user's active subscription
export const getUserSubscription = async (): Promise<Subscription | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  
  const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');
  const q = query(subscriptionsRef, where('status', 'in', ['active', 'trialing']));
  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) return null;
  
  const doc = querySnapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data()
  } as Subscription;
};

// Subscribe to subscription changes
export const subscribeToSubscriptionChanges = (
  callback: (subscription: Subscription | null) => void
): (() => void) => {
  const user = auth.currentUser;
  if (!user) {
    callback(null);
    return () => {};
  }
  
  const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');
  
  return onSnapshot(subscriptionsRef, (snapshot) => {
    const activeSubscription = snapshot.docs.find(doc => {
      const data = doc.data();
      return data.status === 'active' || data.status === 'trialing';
    });
    
    if (activeSubscription) {
      callback({
        id: activeSubscription.id,
        ...activeSubscription.data()
      } as Subscription);
    } else {
      callback(null);
    }
  });
};

// Create customer portal session
export const createPortalSession = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  
  // Use the Firebase callable function with region specification
  const functionRef = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
  const { data } = await functionRef({
    returnUrl: `${window.location.origin}/settings`,
  });
  
  return (data as any).url;
};

// Check if user has access (active subscription)
export const checkAccess = async (): Promise<boolean> => {
  const subscription = await getUserSubscription();
  return subscription !== null && 
    (subscription.status === 'active' || subscription.status === 'trialing');
}; 